/**
 * Represents the set of tokens returned by the authorization server.
 */
export interface TokenModel {
  /** The OAuth 2.0 access token, used to authorize API calls. */
  access_token: string;
  /** The OpenID Connect ID token, containing user identity claims. */
  id_token: string;
  /** Token used to obtain new access tokens when the current one expires. */
  refresh_token: string;
  /** Indicates the type of token, typically “Bearer”. */
  token_type: string;
  /** Lifetime in seconds of the access token before it expires. */
  expires_in: number;
  /** (Optional) Scopes granted by the authorization server. */
  scope?: string;
}

/**
 * The payload extracted from an ID token, containing user and session claims.
 */
export interface TokenPayload {
  /** Subject identifier for the user (unique user ID). */
  sub: string;
  /** Expiration time as a UNIX timestamp (in seconds). */
  exp: number;
  /** Issued-at time as a UNIX timestamp (in seconds). */
  iat: number;
}

/**
 * Model sent to exchange an authorization code or complete a challenge.
 */
export interface CodeExchangeRequestModel {
  /** Action to perform, e.g. "ExchangeCode" or "RespondToChallenge". */
  action: string;
  /** The name of the challenge, if any (e.g. "CUSTOM_CHALLENGE"). */
  challenge_name: string;
  challenge_data: {
    /** ID that tracks this particular auth exchange session. */
    auth_progress_id: string;
    /** Grant type for the exchange, e.g. "authorization_code". */
    grant_type: string;
    /** The authorization code received from the auth server. */
    code: string;
    /** The PKCE code verifier associated with the original code challenge. */
    code_verifier: string;
    /** (Optional) Reference source for tracking purposes. */
    ref_source?: string;
  };
}

/**
 * Response model wrapping the tokens returned after a successful code exchange.
 */
export interface CodeExchangeResponseModel {
  /** The set of tokens (access, ID, refresh) issued by the auth server. */
  tokens: TokenModel;
}

/**
 * Model sent to verify a link login request, typically after a user clicks a link in their email/SMS/whatsapp.
 */
export interface VerifyLinkLoginRequestModel {
  /** Action to perform, e.g. "ExchangeCode" or "RespondToChallenge". */
  action: string;
  /** The name of the challenge, if any (e.g. "CUSTOM_CHALLENGE"). */
  challenge_name: string;
  challenge_data: {
    /** ID that tracks this particular auth exchange session. */
    auth_progress_id: string;
    /** The client ID associated with the application */
    client_id: string;
    /** logged user id from local storage */
    logged_id?: string;
  };
}

/**
 * Response model wrapping the tokens and additional data returned after verifying a link login request.
 */
export interface VerifyLinkLoginResponseModel {
  /** The additional data required for the link login, such as redirect URL and user details. */
  additional_data: LinkLoginAdditionalData;
  /** The set of tokens (access, ID, refresh) issued by the auth server. */
  tokens?: TokenModel;
}

/**
 * Additional data Model required for link login, such as redirect URL and user details.
 */

/**
 * Represents additional data required for link login functionality.
 * This interface includes information about the visitor and their 
 * intended redirection after login.
 */
export interface LinkLoginAdditionalData {
  /** landing page e.g ticket details / assign tickets / checkout payment/ accept invite */
  redirect: string;
  /** First name of the visitor */
  first_name: string;
  /** Last name of the visitor */
  last_name: string;
  /** State of the visitor */
  state: string;
  /** City of the visitor */
  city: string;
  /** Category of the visitor */
  category: string;
  /** ticket code if auto login to view ticket */
  ticket_code?: string;
  /** invitation code if auto login to accept invite */
  invite_code?: string;
  /** summary_id code if auto login to abandoned checkout */
  summary_id?: string;
  /** Event code associated with the visitor redirect activity */
  eventcode?: string;
  /** Reference source */
  ref_source?: string;
}

export const defaultRouteAfterLogin = '/reg/select-ticket';